import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from 'sonner';
import { Bell, BellOff, Smartphone, XCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(char => char.charCodeAt(0)));
}

const Settings = () => {
  const { user, isAdmin } = useAuth();
  const [copied, setCopied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("notifications");
  
  // Configuration démo
  const [demoConfig, setDemoConfig] = useState({
    enabled: false, site_name: "Conciergerie Pro",
    contact_email: "", contact_phone: "", banner_message: ""
  });
  const [savingDemo, setSavingDemo] = useState(false);
  
  // Notifications
  const [notifUsers, setNotifUsers] = useState([]);
  const [notifLoading, setNotifLoading] = useState(true);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [debugInfo, setDebugInfo] = useState('');
  
  // WhatsApp template
  const [waTemplate, setWaTemplate] = useState("Bonjour, le logement au {adresse} est pret.\nRAS,\nM&M Conciergerie");
  const [savingWa, setSavingWa] = useState(false);
  
  useEffect(() => {
    // Check push support - more robust detection
    const hasSW = 'serviceWorker' in navigator;
    const hasPN = 'PushManager' in window;
    const hasNotif = 'Notification' in window;
    const supported = hasSW && (hasPN || hasNotif);
    setPushSupported(supported);
    
    // Debug info for troubleshooting
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    const ua = navigator.userAgent;
    const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua);
    setDebugInfo(`SW:${hasSW} PM:${hasPN} N:${hasNotif} PWA:${isStandalone} Safari:${isSafari}`);
    
    if (hasNotif && Notification.permission) setPushPermission(Notification.permission);
    loadDemoConfig();
    loadNotificationSettings();
    checkSubscription();
    loadWhatsappTemplate();
  }, []);

  // --- WhatsApp Template ---
  const loadWhatsappTemplate = async () => {
    try {
      const res = await axios.get(`${API}/whatsapp-template`);
      if (res.data.template) setWaTemplate(res.data.template);
    } catch (e) { /* use default */ }
  };

  const handleSaveWaTemplate = async () => {
    setSavingWa(true);
    try {
      await axios.put(`${API}/whatsapp-template`, { template: waTemplate });
      toast.success("Message WhatsApp sauvegarde !");
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSavingWa(false);
    }
  };

  // --- Demo Config ---
  const loadDemoConfig = async () => {
    try {
      const response = await axios.get(`${API}/demo/config`);
      setDemoConfig(response.data);
    } catch (error) {
      console.log("Config demo non disponible");
    }
  };

  const handleSaveDemoConfig = async () => {
    setSavingDemo(true);
    try {
      await axios.put(`${API}/demo/config`, demoConfig);
      toast.success("Configuration demo sauvegardee !");
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSavingDemo(false);
    }
  };

  const handleToggleDemo = async () => {
    try {
      const response = await axios.post(`${API}/demo/toggle`);
      setDemoConfig({ ...demoConfig, enabled: response.data.enabled });
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Erreur lors du changement de mode");
    }
  };

  // --- API Key ---
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRefreshApiKey = async () => {
    setRefreshing(true);
    try {
      const response = await axios.post(`${API}/auth/refresh-api-key`);
      toast.success("API Key regeneree avec succes !");
      const updatedUser = { ...user, api_key: response.data.api_key };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      window.location.reload();
    } catch (error) {
      toast.error("Erreur lors de la regeneration de l'API Key");
    } finally {
      setRefreshing(false);
    }
  };

  // --- Notifications ---
  const loadNotificationSettings = async () => {
    setNotifLoading(true);
    try {
      const response = await axios.get(`${API}/notification-settings`);
      setNotifUsers(response.data);
    } catch (error) {
      console.log("Notification settings non disponible");
    } finally {
      setNotifLoading(false);
    }
  };

  const checkSubscription = async () => {
    // Check backend for subscription status (source of truth)
    try {
      const response = await axios.get(`${API}/push/status`);
      setIsSubscribed(response.data.has_subscription);
    } catch (e) {
      console.log('Check push status error:', e);
      setIsSubscribed(false);
    }
  };

  // Detect if running as installed PWA
  const isInstalledPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

  const handleSubscribePush = async () => {
    if (!pushSupported) {
      toast.error("Les notifications push ne sont pas supportees par ce navigateur");
      return;
    }

    // iOS Safari detection
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS && !isInstalledPWA) {
      toast.error("Sur iPhone, installez d'abord l'app sur votre ecran d'accueil (Partager > Sur l'ecran d'accueil), puis activez les notifications depuis l'app.", { duration: 8000 });
      return;
    }

    setSubscribing(true);
    try {
      // Step 1: Request permission
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      if (permission !== 'granted') {
        toast.error("Vous devez autoriser les notifications dans votre navigateur");
        setSubscribing(false);
        return;
      }

      // Step 2: Register service worker
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;

      // Step 3: Remove any old subscription
      const existingSub = await registration.pushManager.getSubscription();
      if (existingSub) await existingSub.unsubscribe();

      // Step 4: Subscribe with VAPID key
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      // Step 5: Save to backend
      const subJson = subscription.toJSON();
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subJson.endpoint,
        keys: subJson.keys
      });

      // Step 6: Verify backend saved it
      const statusResp = await axios.get(`${API}/push/status`);
      if (statusResp.data.has_subscription) {
        setIsSubscribed(true);
        toast.success("Notifications activees sur cet appareil !");
        loadNotificationSettings();
      } else {
        toast.error("L'activation a echoue. Reessayez.");
      }
    } catch (error) {
      console.error("Erreur activation push:", error);
      const msg = error.response?.data?.detail || error.message || "";
      const isIOS2 = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS2) {
        toast.error("Sur iPhone, les notifications ne fonctionnent que depuis l'app installee sur l'ecran d'accueil.", { duration: 6000 });
      } else {
        toast.error(`Erreur: ${msg || "Verifiez les parametres de votre navigateur."}`, { duration: 5000 });
      }
    } finally {
      setSubscribing(false);
    }
  };

  const handleUnsubscribe = async () => {
    try {
      const registration = await navigator.serviceWorker.getRegistration('/sw.js');
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) await subscription.unsubscribe();
      }
      await axios.delete(`${API}/push/unsubscribe`);
      setIsSubscribed(false);
      toast.success("Notifications desactivees sur cet appareil");
      loadNotificationSettings();
    } catch (error) {
      toast.error("Erreur lors de la desactivation");
    }
  };

  const handleToggleUser = async (userId, currentValue) => {
    setNotifUsers(prev => prev.map(u => u.user_id === userId ? { ...u, receive_notifications: !currentValue } : u));
    try {
      await axios.put(`${API}/notification-settings/${userId}`, { receive_notifications: !currentValue });
      toast.success(!currentValue ? "Notifications activees" : "Notifications desactivees");
    } catch (error) {
      setNotifUsers(prev => prev.map(u => u.user_id === userId ? { ...u, receive_notifications: currentValue } : u));
      toast.error("Erreur lors de la mise a jour");
    }
  };

  const handleTestNotification = async () => {
    try {
      const resp = await axios.post(`${API}/push/test`);
      toast.success(`Notification de test envoyee ! (${resp.data.sent_count} appareil(s))`);
    } catch (error) {
      const detail = error.response?.data?.detail || "";
      if (detail.includes("Aucun appareil")) {
        toast.error("Aucun appareil enregistre. Cliquez 'Desactiver' puis 'Activer' pour re-enregistrer.", { duration: 5000 });
        setIsSubscribed(false);
      } else {
        toast.error(`Erreur: ${detail || "Echec de l'envoi du test"}`);
      }
    }
  };

  const tabs = [
    { id: "notifications", label: "Notifications", icon: "🔔" },
    { id: "whatsapp", label: "WhatsApp", icon: "💬" },
    { id: "compte", label: "Compte", icon: "👤" },
    { id: "siri", label: "Siri", icon: "🎤" },
    ...(isAdmin() ? [{ id: "demo", label: "Mode Demo", icon: "🎯" }] : []),
  ];

  return (
    <div className="max-w-4xl mx-auto py-6 px-4" data-testid="settings-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Parametres</h1>
        <p className="text-gray-500 mt-1">Configuration de votre compte et de l'application</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg overflow-x-auto" data-testid="settings-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <span>{tab.icon}</span> {tab.label}
          </button>
        ))}
      </div>

      {/* === TAB: Notifications === */}
      {activeTab === "notifications" && (
        <div className="space-y-6">
          {/* Mon appareil */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="device-section">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Smartphone className="h-5 w-5 text-blue-600" />
              Mon appareil
            </h2>
            <div className="space-y-4">
              {/* iOS not-PWA warning */}
              {/iPad|iPhone|iPod/.test(navigator.userAgent) && !isInstalledPWA && (
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <p className="font-medium text-red-800 text-sm">iPhone detecte - Installation requise</p>
                  <p className="text-sm text-red-700 mt-1">
                    Sur iPhone, les notifications ne fonctionnent que depuis l'app installee. 
                    Appuyez sur <strong>Partager</strong> (icone en bas de Safari) puis <strong>"Sur l'ecran d'accueil"</strong>, 
                    ensuite ouvrez l'app depuis votre ecran d'accueil et revenez ici.
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Notifications push</p>
                  <p className="text-sm text-gray-500">
                    {!pushSupported ? "Non supporte par ce navigateur"
                      : pushPermission === 'denied' ? "Bloque par le navigateur"
                      : isSubscribed ? "Active sur cet appareil"
                      : "Non active sur cet appareil"}
                  </p>
                  {!pushSupported && (
                    <p className="text-xs text-gray-400 mt-1">Diagnostic: {debugInfo}</p>
                  )}
                </div>
                <div>
                  {!pushSupported ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-200 text-gray-600">
                      <XCircle className="h-4 w-4 mr-1" /> Non supporte
                    </span>
                  ) : pushPermission === 'denied' ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-100 text-red-700">
                      <XCircle className="h-4 w-4 mr-1" /> Bloque
                    </span>
                  ) : isSubscribed ? (
                    <button onClick={handleUnsubscribe}
                      className="inline-flex items-center px-4 py-2 rounded-lg text-sm bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                      data-testid="unsubscribe-btn">
                      <BellOff className="h-4 w-4 mr-2" /> Desactiver
                    </button>
                  ) : (
                    <button onClick={handleSubscribePush} disabled={subscribing}
                      className="inline-flex items-center px-4 py-2 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
                      data-testid="subscribe-btn">
                      <Bell className="h-4 w-4 mr-2" />
                      {subscribing ? "Activation..." : "Activer les notifications"}
                    </button>
                  )}
                </div>
              </div>

              {isSubscribed && (
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Tester les notifications</p>
                    <p className="text-sm text-gray-500">Envoyez une notification de test</p>
                  </div>
                  <button onClick={handleTestNotification}
                    className="inline-flex items-center px-4 py-2 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                    data-testid="test-notification-btn">
                    Tester
                  </button>
                </div>
              )}

              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="font-medium text-amber-800 text-sm">Installer l'application</p>
                <ul className="text-sm text-amber-700 mt-2 ml-4 list-disc space-y-1">
                  <li><strong>iPhone (Safari)</strong> : Partager &rarr; "Sur l'ecran d'accueil"</li>
                  <li><strong>Android (Chrome)</strong> : Menu &rarr; "Ajouter a l'ecran d'accueil"</li>
                  <li><strong>PC/Mac (Chrome)</strong> : Icone d'installation dans la barre d'adresse</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Notification settings per user */}
          {isAdmin() && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="notification-settings-section">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                <Bell className="h-5 w-5 text-red-600" />
                Qui recoit les notifications ?
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Quand une mission est terminee, ces utilisateurs recoivent une notification.
              </p>
              {notifLoading ? (
                <div className="text-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600 mx-auto"></div></div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {notifUsers.map((u) => (
                    <div key={u.user_id} className="flex items-center justify-between py-4" data-testid={`user-notification-row-${u.user_id}`}>
                      <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${u.role === 'admin' ? 'bg-red-600' : 'bg-blue-500'}`}>
                          {(u.full_name || u.email || '?').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{u.full_name || u.email}</p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm text-gray-500">{u.email}</span>
                            {u.role === 'admin' && <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded">Admin</span>}
                            {u.has_subscription && (
                              <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded flex items-center gap-1">
                                <Smartphone className="h-3 w-3" /> {u.subscription_count} appareil{u.subscription_count > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <button onClick={() => handleToggleUser(u.user_id, u.receive_notifications)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${u.receive_notifications ? 'bg-green-500' : 'bg-gray-300'}`}
                        data-testid={`toggle-notification-${u.user_id}`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${u.receive_notifications ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                  ))}
                  {notifUsers.length === 0 && <p className="text-gray-500 text-center py-6">Aucun utilisateur</p>}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* === TAB: WhatsApp === */}
      {activeTab === "whatsapp" && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="whatsapp-template-section">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Message WhatsApp predefini</h2>
          <p className="text-sm text-gray-500 mb-4">
            Personnalisez le message envoye aux proprietaires. Utilisez <code className="bg-gray-100 px-1.5 py-0.5 rounded text-red-600 text-xs">{'{adresse}'}</code> pour inserer l'adresse du logement.
          </p>
          <textarea
            value={waTemplate}
            onChange={(e) => setWaTemplate(e.target.value)}
            rows={5}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            data-testid="whatsapp-template-input"
          />
          <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
            <p className="text-sm font-medium text-green-800 mb-1">Apercu :</p>
            <p className="text-sm text-green-700 whitespace-pre-line">{waTemplate.replace('{adresse}', '12 rue Example, Paris')}</p>
          </div>
          <div className="mt-4 flex justify-end">
            <button onClick={handleSaveWaTemplate} disabled={savingWa}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
              data-testid="save-whatsapp-template-btn">
              {savingWa ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
          </div>
        </div>
      )}

      {/* === TAB: Compte === */}
      {activeTab === "compte" && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Informations du Compte</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom complet</label>
              <div className="text-gray-900 font-medium">{user?.full_name || "-"}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="text-gray-900">{user?.email}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user?.role === "admin" ? "bg-red-100 text-red-800" : "bg-blue-100 text-blue-800"}`}>
                {user?.role === "admin" ? "Administrateur" : "Utilisateur"}
              </span>
            </div>
          </div>
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2 text-sm">Securite</h3>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>Ne partagez jamais votre API Key</li>
              <li>Regenerez votre cle si elle a ete compromise</li>
            </ul>
          </div>
        </div>
      )}

      {/* === TAB: Siri === */}
      {activeTab === "siri" && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Integration Siri</h2>
          <p className="text-gray-600 mb-4">Utilisez cette cle API dans vos raccourcis Siri pour acceder a votre planning.</p>
          {user?.api_key ? (
            <>
              <label className="block text-sm font-medium text-gray-700 mb-2">Votre API Key Siri</label>
              <div className="flex items-center space-x-2">
                <div className="flex-1 bg-gray-50 border border-gray-300 rounded p-3 font-mono text-sm break-all">{user.api_key}</div>
                <button onClick={() => copyToClipboard(user.api_key)} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700" data-testid="copy-api-key-btn">
                  {copied ? "Copie !" : "Copier"}
                </button>
              </div>
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2 text-sm">Configuration dans Raccourcis Siri</h3>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Ouvrez l'app "Raccourcis" sur iPhone</li>
                  <li>Ajoutez "Obtenir le contenu de l'URL"</li>
                  <li>URL : <code className="bg-white px-1 rounded">{BACKEND_URL}/api/missions</code></li>
                  <li>En-tete : <code className="bg-white px-1 rounded">Authorization</code> = <code className="bg-white px-1 rounded">Bearer {user.api_key}</code></li>
                </ol>
              </div>
              <div className="mt-4">
                <button onClick={handleRefreshApiKey} disabled={refreshing} className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50">
                  {refreshing ? "Regeneration..." : "Regenerer l'API Key"}
                </button>
                <p className="text-xs text-gray-500 mt-1">Cela invalidera vos raccourcis Siri existants</p>
              </div>
            </>
          ) : (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-yellow-800 text-sm">Vous n'avez pas d'API Key. Contactez un administrateur.</p>
            </div>
          )}
        </div>
      )}

      {/* === TAB: Demo === */}
      {activeTab === "demo" && isAdmin() && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Configuration Mode Demo</h2>
            <button onClick={handleToggleDemo}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${demoConfig.enabled ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-green-500 hover:bg-green-600 text-white'}`}>
              {demoConfig.enabled ? 'Desactiver Demo' : 'Activer Demo'}
            </button>
          </div>
          <div className={`p-3 rounded-lg mb-4 ${demoConfig.enabled ? 'bg-orange-50 border border-orange-200' : 'bg-gray-50'}`}>
            <p className="text-sm">{demoConfig.enabled ? 'Le mode demo est ACTIF' : 'Le mode demo est desactive'}</p>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom du site</label>
              <input type="text" value={demoConfig.site_name} onChange={(e) => setDemoConfig({ ...demoConfig, site_name: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email de contact</label>
                <input type="email" value={demoConfig.contact_email} onChange={(e) => setDemoConfig({ ...demoConfig, contact_email: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telephone (optionnel)</label>
                <input type="text" value={demoConfig.contact_phone || ''} onChange={(e) => setDemoConfig({ ...demoConfig, contact_phone: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message personnalise (optionnel)</label>
              <input type="text" value={demoConfig.banner_message || ''} onChange={(e) => setDemoConfig({ ...demoConfig, banner_message: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="flex justify-end">
              <button onClick={handleSaveDemoConfig} disabled={savingDemo} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
                {savingDemo ? 'Sauvegarde...' : 'Sauvegarder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
